import { z } from 'zod';

/**
 * Validation for a comma-separated list of CC email addresses.
 *
 * Empty string is permitted (no CC). Anything else is split on commas,
 * trimmed, and each token must look like an email. We surface a single
 * validation message — the dialog also normalises the value at submit.
 */
function csvEmailsRefiner(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return trimmed
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0)
    .every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

/**
 * Zod schema for the Send-Email dialog.
 *
 * Field shapes follow the playbook §3.5 rules — every "optional-feeling" field
 * is modelled as `z.string()` with empty-string default so `z.input` and
 * `z.output` stay identical (no react-hook-form Resolver mismatch).
 */
export const quoteEmailSchema = z.object({
  recipientEmail: z
    .string()
    .trim()
    .min(1, 'Recipient email is required')
    .email('Enter a valid email address'),
  /** Comma-separated email list. Empty string = no CC. */
  ccEmails: z
    .string()
    .max(2000, 'CC list must be 2000 characters or fewer')
    .refine(csvEmailsRefiner, {
      message: 'One or more CC emails are invalid',
    }),
  subject: z
    .string()
    .trim()
    .min(1, 'Subject is required')
    .max(200, 'Subject must be 200 characters or fewer'),
  bodyMessage: z
    .string()
    .trim()
    .min(1, 'Message body is required')
    .max(8000, 'Message must be 8000 characters or fewer'),
  attachPreviewAsPdf: z.boolean(),
});

export type QuoteEmailFormValues = z.infer<typeof quoteEmailSchema>;

/** Default form values — safe for `useForm({ defaultValues: ... })` from mount. */
export const defaultQuoteEmailValues: QuoteEmailFormValues = {
  recipientEmail: '',
  ccEmails: '',
  subject: '',
  bodyMessage: '',
  attachPreviewAsPdf: true,
};

/**
 * Normalise CC string to a `string[]` for API payloads.
 * Returns `[]` when the field is empty.
 */
export function parseCcEmails(value: string): string[] {
  return value
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
}

/**
 * Compose a sensible default subject when the backend doesn't ship a template.
 * Example: "Quote QT-001234 from Euro Scaffolds".
 */
export function buildDefaultSubject(params: {
  quoteNumber?: string;
  companyName?: string;
}): string {
  const qn = params.quoteNumber?.trim() || 'your project';
  const co = params.companyName?.trim() || 'us';
  return `Quote ${qn} from ${co}`;
}

/**
 * Compose a sensible default email body when the backend doesn't ship a
 * template. Plain text — keeps line breaks for nice rendering in clients.
 */
export function buildDefaultBody(params: {
  recipientName?: string;
  quoteNumber?: string;
  companyName?: string;
}): string {
  const greeting = params.recipientName?.trim()
    ? `Hi ${params.recipientName.trim()},`
    : 'Hello,';
  const co = params.companyName?.trim() || 'our team';
  const qn = params.quoteNumber?.trim() || 'the attached quote';
  return [
    greeting,
    '',
    `Please find ${qn} attached for your review.`,
    '',
    "We're happy to walk through the line items or discuss adjustments at your convenience — just let us know what works best.",
    '',
    'Kind regards,',
    co,
  ].join('\n');
}
